import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { CustomSection, CustomSectionPosition } from "./types";

type CustomSectionSectionProps = {
  sections: CustomSection[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, key: keyof CustomSection, value: string) => void;
};

const POSITIONS: { value: CustomSectionPosition; label: string }[] = [
  { value: "top", label: "Top (Before Intro)" },
  { value: "before_scoring", label: "Before Scoring Criteria" },
  { value: "before_tiers", label: "Before Tier Classification" },
  { value: "before_keywords", label: "Before Keyword Signals" },
  { value: "bottom", label: "Bottom (After Output Requirements)" },
];

export default function CustomSectionSection({
  sections,
  onAdd,
  onRemove,
  onChange,
}: CustomSectionSectionProps) {
  return (
    <div className="space-y-4">
      {sections.length === 0 ? (
        <p className="text-sm italic text-gray-400 dark:text-gray-500">
          No custom sections added. You can use this to inject constraints, formatting rules, or company policies into the prompt.
        </p>
      ) : (
        <div className="space-y-4">
          {sections.map((section, index) => (
            <div
              key={index}
              className="relative flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 pr-10 dark:border-gray-700 dark:bg-gray-800"
            >
              <button
                type="button"
                className="absolute right-3 top-3 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 transition-colors"
                onClick={() => onRemove(index)}
                title="Remove this section"
              >
                <TrashIcon className="h-5 w-5" />
              </button>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Section Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => onChange(index, "title", e.target.value)}
                    placeholder="e.g. CRITICAL FAIRNESS REQUIREMENTS"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 dark:border-gray-600 dark:bg-darkbg dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Insert Position
                  </label>
                  <select
                    value={section.position}
                    onChange={(e) => onChange(index, "position", e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 dark:border-gray-600 dark:bg-darkbg dark:text-gray-100"
                  >
                    {POSITIONS.map((pos) => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Content (Markdown supported)
                </label>
                <textarea
                  value={section.content}
                  onChange={(e) => onChange(index, "content", e.target.value)}
                  placeholder="- Candidates name\n- College name\n- Any personal characteristics..."
                  className="h-32 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 dark:border-gray-600 dark:bg-darkbg dark:text-gray-100"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="flex items-center gap-1.5 rounded-lg border border-entntblue/40 bg-entntblue/5 px-3 py-2 text-sm font-medium text-entntblue hover:bg-entntblue/10 transition-colors"
        onClick={onAdd}
      >
        <PlusIcon className="h-4 w-4" /> Add Custom Section
      </button>
    </div>
  );
}
